import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, User, Package, FileText, BarChart3, MessageSquare, ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { professionalClientService } from '../../services/professionalClientService';
import ClientInfoTab from './components/ClientInfoTab';
import ShipmentsTab from './components/ShipmentsTab';
import DocumentsTab from './components/DocumentsTab';
import AnalyticsTab from './components/AnalyticsTab';
import CommunicationsTab from './components/CommunicationsTab';
import Icon from '../../components/AppIcon';


const DetailedProfessionalClientProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams?.get('id');

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (clientId) {
      loadClientData();
    }
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await professionalClientService?.getClientById(clientId);
      if (!data) {
        setError('Client professionnel non trouvé');
        return;
      }
      setClient(data);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement du client');
      console.error('Error loading client:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'info', label: 'Informations', icon: User },
    { id: 'shipments', label: 'Expéditions', icon: Package },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'analytics', label: 'Analyse & Statistiques', icon: BarChart3 },
    { id: 'communications', label: 'Communications', icon: MessageSquare }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="ml-4 text-gray-600">Chargement du profil client...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 text-lg font-medium">{error || 'Client non trouvé'}</p>
            <button
              onClick={() => navigate('/professional-clients-management-dashboard')}
              className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <BreadcrumbTrail customItems={[
        { label: 'Administration', path: '/admin-shipments-dashboard' },
        { label: 'Clients Pro', path: '/professional-clients-management-dashboard' },
        { label: client?.companyName, path: `/detailed-professional-client-profile?id=${clientId}` }
      ]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Building2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{client?.companyName}</h1>
                <p className="text-gray-600 mt-1">{client?.contactName}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  {client?.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {client?.email}
                    </div>
                  )}
                  {client?.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {client?.phone}
                    </div>
                  )}
                  {client?.address && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {client?.address}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/professional-clients-management-dashboard')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Expéditions totales</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{client?.totalShipments || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Revenu total</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{(client?.totalRevenue || 0)?.toFixed(2)}€</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Type de client</p>
              <p className="text-lg font-medium text-gray-900 mt-1 capitalize">{client?.clientType}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Statut du compte</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                client?.accountStatus === 'active' ? 'bg-green-100 text-green-800' :
                client?.accountStatus === 'pending'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {client?.accountStatus === 'active' ? 'Actif' :
                 client?.accountStatus === 'pending' ? 'En attente' : 'Inactif'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab?.id
                        ? 'border-green-600 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'info' && <ClientInfoTab client={client} onUpdate={loadClientData} />}
            {activeTab === 'shipments' && <ShipmentsTab clientId={clientId} />}
            {activeTab === 'documents' && <DocumentsTab clientId={clientId} />}
            {activeTab === 'analytics' && <AnalyticsTab clientId={clientId} />}
            {activeTab === 'communications' && <CommunicationsTab clientId={clientId} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedProfessionalClientProfile;